/**
 * Shared design tokens for the responsive redesign.
 *
 * Owned by Track 5 (foundation files). Other tracks import from here so we
 * stay consistent across pages. All values are static `as const` — safe to
 * use as both runtime values and TypeScript types.
 *
 * Usage:
 *   import { typography, space, colors, radius, breakpoints } from '@/lib/designTokens';
 *
 *   <h1 style={{ ...typography.h1, color: colors.textPrimary }}>...</h1>
 *
 * Spec source: parallel-agent task brief 2026-05-01.
 */

export const breakpoints = {
  mobile: 0,
  tablet: 768,
  desktop: 1024,
} as const;

export type Breakpoints = typeof breakpoints;

/**
 * Typography scale. fontSize is in px (numbers — React inline style accepts
 * raw numbers and treats them as px). letterSpacing is in px as well.
 */
export const typography = {
  display: { fontSize: 32, fontWeight: 800, letterSpacing: -0.5 },
  h1:      { fontSize: 24, fontWeight: 800, letterSpacing: -0.4 },
  h2:      { fontSize: 20, fontWeight: 700 },
  h3:      { fontSize: 16, fontWeight: 700 },
  body:    { fontSize: 14, fontWeight: 500 },
  caption: { fontSize: 12, fontWeight: 600 },
  micro:   { fontSize: 11, fontWeight: 600 },
} as const;

export type TypographyScale = keyof typeof typography;

/**
 * Spacing scale. Numbers are px values — pass directly to React inline
 * styles (gap, padding, margin) without unit suffix.
 */
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 32,
  xxxl: 48,
} as const;

export type Space = keyof typeof space;

/**
 * Color palette. All colors are dark-theme first.
 *
 *  - bg:            page background (true black to look right on OLED)
 *  - surface:       low-elevation card / chip background
 *  - surface2:      hover / active surface
 *  - textPrimary:   default body text
 *  - textSecondary: secondary copy, labels
 *  - textMuted:     placeholder / disabled
 *  - accent:        brand red — primary CTA
 *  - accentWarm:    warm orange for highlights / urgency
 *  - success:       online / available indicator
 *  - border:        hairline divider
 */
export const colors = {
  bg: '#000',
  surface: 'rgba(255,255,255,0.04)',
  surface2: 'rgba(255,255,255,0.08)',
  textPrimary: '#fff',
  textSecondary: 'rgba(255,255,255,0.7)',
  textMuted: 'rgba(255,255,255,0.4)',
  accent: '#e11d48',
  accentWarm: '#ff6b35',
  success: '#4ADE80',
  border: 'rgba(255,255,255,0.06)',
} as const;

export type ColorToken = keyof typeof colors;

/**
 * Border-radius scale. Numbers are px.
 */
export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  pill: 999,
} as const;

export type Radius = keyof typeof radius;

/**
 * Shadow scale. Used on elevated cards / floating menus.
 */
export const shadows = {
  none: 'none',
  sm: '0 2px 8px rgba(0,0,0,0.32)',
  md: '0 8px 24px rgba(0,0,0,0.4)',
  lg: '0 16px 48px rgba(0,0,0,0.55)',
} as const;

export type Shadow = keyof typeof shadows;

/**
 * Standardized "tease" blur applied to ANY user photo we want to show
 * locked / paywalled — likes-you, profile viewers, match-lock nudge,
 * subscription offer, push prompt, etc. 20% blur strength: composition,
 * skin tone, and pose read clearly while faces and fine detail stay
 * obscured. Brightness lifted slightly so the photo doesn't look dead.
 *
 * Whenever you add a NEW locked-photo surface (frontend OR email), use
 * `blurredPhotoFilter`. Don't introduce a one-off blur value — every
 * locked photo across the app should look the same.
 */
export const blurredPhotoFilter = 'blur(4px) brightness(0.97)' as const;

/**
 * Common z-index layers — kept here so we don't accumulate magic numbers
 * across components.
 */
export const zIndex = {
  base: 0,
  card: 10,
  sidebar: 30,
  bottomNav: 9000,
  banner: 9500,
  modal: 10000,
  toast: 11000,
} as const;
